import Config from '@config';
import { normalizeInfiniteQueryData } from '@gasbi/shared/common/features/utils';
import { createApi } from '@reduxjs/toolkit/query/react';

import { CACHE_EXPIRATION_TIME_REFERENCE_BOOK } from '@/features/constants';
import { infiniteQueryOptions } from '@/features/utils';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

import { FiltersGrid, Pagination } from '../types';
import {
  MeteringDeviceType,
  MeteringDeviceTypeGrid,
  MeteringDeviceTypeRequestBody,
} from './types';

export const meteringDeviceTypeApi = createApi({
  reducerPath: 'meteringDeviceTypeApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${Config.VITE_API_URL}/metering-device-types`,
  }),
  tagTypes: ['MeteringDeviceType', 'MeteringDeviceTypes'],
  keepUnusedDataFor: CACHE_EXPIRATION_TIME_REFERENCE_BOOK,
  endpoints: builder => ({
    getMeteringDeviceType: builder.query<MeteringDeviceType[], FiltersGrid>({
      query: (params = {}) => {
        return {
          url: `/query`,
          params,
        };
      },
      providesTags: () => ['MeteringDeviceType', 'MeteringDeviceTypes'],
    }),
    getMeteringDeviceTypeI: builder.infiniteQuery<
      MeteringDeviceTypeGrid[],
      FiltersGrid,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: `/query`,
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['MeteringDeviceType', 'MeteringDeviceTypes'],
    }),
    getMeteringDeviceTypeById: builder.query<MeteringDeviceType, string>({
      query: id => ({
        url: `/${id}`,
      }),
      providesTags: () => ['MeteringDeviceType', 'MeteringDeviceTypes'],
    }),
    createMeteringDeviceType: builder.mutation<
      MeteringDeviceTypeGrid,
      MeteringDeviceTypeRequestBody
    >({
      query: data => ({
        url: '/',
        method: 'POST',
        data,
      }),
      invalidatesTags: result => [
        'MeteringDeviceType',
        { type: 'MeteringDeviceType', id: result?.id },
      ],
    }),
    updateMeteringDeviceType: builder.mutation<
      MeteringDeviceTypeGrid,
      MeteringDeviceTypeRequestBody
    >({
      query: data => ({
        url: `/${data.id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: result => [
        'MeteringDeviceType',
        { type: 'MeteringDeviceType', id: result?.id },
      ],
    }),
    deleteMeteringDeviceType: builder.mutation<void, string>({
      query: id => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, id) => [
        'MeteringDeviceType',
        { type: 'MeteringDeviceType', id },
      ],
    }),
  }),
});

export const {
  useGetMeteringDeviceTypeByIdQuery,
  useCreateMeteringDeviceTypeMutation,
  useUpdateMeteringDeviceTypeMutation,
  useDeleteMeteringDeviceTypeMutation,
} = meteringDeviceTypeApi;

export const useGetMeteringDeviceTypeInfiniteQuery = normalizeInfiniteQueryData(
  meteringDeviceTypeApi.useGetMeteringDeviceTypeIInfiniteQuery,
);
